import WrappedPicker from './WrappedPicker';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { workflowStudies } from '../content/workflowStudies';
import { workflowPages } from '../content/workflowPages';
import '../styles/workflow-studies.css';
import WorkflowSystems from './WorkflowSystems';

const functions = [...new Set(workflowStudies.map(study => study.function))];

export default function CaseStudyCarousel() {
  const [industry, setIndustry] = useState('All industries');
  const [businessFunction, setBusinessFunction] = useState('All work');
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detailView, setDetailView] = useState('Overview');
  const [showAll, setShowAll] = useState(false);
  const filtered = workflowStudies.filter(study =>
    (industry === 'All industries' || study.industry === industry) &&
    (businessFunction === 'All work' || study.function === businessFunction) &&
    Object.values(study).flat().join(' ').toLowerCase().includes(query.trim().toLowerCase())
  );
  const visible = showAll ? filtered : filtered.slice(0, 6);
  const reset = () => { setIndustry('All industries'); setBusinessFunction('All work'); setQuery(''); setShowAll(false); };

  return (
    <section id="case-studies" className="workflow-studies" aria-labelledby="workflows-heading">
      <div className="ws-shell">
        <div className="ws-heading">
          <div>
            <p className="ws-kicker">Workflow examples</p>
            <h2 id="workflows-heading">Find a workflow that fits your business.</h2>
          </div>
          <div className="ws-intro">
            <p>Find a familiar problem. Open a scenario to see the solution, the systems involved and the intended impact.</p>
            <p className="ws-evidence">Browse 18 example workflows. Each describes a proposed solution; none is a claim about completed client work.</p>
          </div>
        </div>

        <p className="mb-6"><Link className="underline text-[#873711]" to="/workflows/">Read the full workflow designs, controls and recovery plans →</Link></p>
        <div className="ws-finder">
          <div className="ws-inputs">
            <WrappedPicker label="Find your industry" value={industry} onChange={value => { setIndustry(value); setShowAll(false); }} choices={['All industries', ...workflowStudies.map(study => study.industry)].map(name => ({ id: name, name }))} />
            <WrappedPicker label="Type of work" value={businessFunction} onChange={value => { setBusinessFunction(value); setShowAll(false); }} choices={['All work', ...functions].map(name => ({ id: name, name }))} />
            <label>Or search the problem
              <input type="search" value={query} onChange={event => { setQuery(event.target.value); setShowAll(false); }} placeholder="Try invoices, stock, onboarding..." />
            </label>
          </div>

        </div>

        <div className="ws-results-header">
          <p role="status">{filtered.length === 0 ? 'No matching scenarios' : `${filtered.length} ${filtered.length === 1 ? 'scenario' : 'scenarios'}${visible.length < filtered.length ? ` · Showing ${visible.length}` : ''}`}</p>
          {(industry !== 'All industries' || businessFunction !== 'All work' || query) && <button type="button" onClick={reset}>Clear filters</button>}
        </div>

        <div className="ws-results">
          {visible.map(study => {
            const isOpen = expanded === study.id;
            return <article className={`ws-study${isOpen ? ' is-open' : ''}`} key={study.id}>
              <h3><button className="ws-study-toggle" type="button" aria-expanded={isOpen} aria-controls={`study-${study.id}`} onClick={() => { setExpanded(isOpen ? null : study.id); setDetailView('Overview'); }}>
                <span className="ws-industry">{study.industry}<span>{study.function}</span></span>
                <span className="ws-title">{study.title}</span>
                <span className="ws-toggle-mark">{isOpen ? 'Close scenario' : 'View scenario'} <span aria-hidden="true">{isOpen ? '−' : '+'}</span></span>
              </button></h3>{workflowPages.some(page => page.id === study.id) && <Link className="inline-block px-5 py-3 text-[#873711] underline" to={`/workflows/${workflowPages.find(page => page.id === study.id)!.slug}/`}>Read the complete design →</Link>}{study.id === "accounting" && <Link className="inline-block px-5 py-3 text-[#873711] underline" to="/blog/accounting-finance-automation/">Read the invoice design →</Link>}
              {isOpen && <div id={`study-${study.id}`} className="ws-detail">
                <div className="ws-detail-nav" role="group" aria-label={`${study.industry} scenario sections`}>
                  {['Overview', 'The workflow', 'Impact'].map(view => <button key={view} type="button" aria-pressed={detailView === view} aria-controls={`study-content-${study.id}`} onClick={() => setDetailView(view)}>{view}</button>)}
                </div>
                <div className="ws-detail-layout">
                  <div id={`study-content-${study.id}`} className="ws-reading">
                    {detailView === 'Overview' && <>
                      <div className="ws-problem"><h4>The problem</h4><p>{study.situation}</p></div>
                      <div className="ws-outcome"><h4>The intended outcome</h4><p>{study.outcome}</p></div>
                      <button className="ws-next" type="button" onClick={() => setDetailView('The workflow')}>See how the workflow works <span aria-hidden="true">→</span></button>
                    </>}
                    {detailView === 'The workflow' && <div className="ws-solution"><h4>How we'd solve it</h4><ol>{study.steps.map((step, index) => <li key={step}><span aria-hidden="true">{index + 1}</span><p>{step}</p></li>)}</ol><div className="ws-control"><h4>People stay in control</h4><p>{study.control}</p></div></div>}
                    {detailView === 'Impact' && <><div className="ws-outcome"><h4>The intended outcome</h4><p>{study.outcome}</p></div><div className="ws-measures"><h4>How we'd measure success</h4><ul>{study.measures.map(measure => <li key={measure}>{measure}</li>)}</ul><p>Agree a baseline before implementation, then compare the same measures after launch.</p></div></>}
                  </div>
                  <WorkflowSystems systems={study.systems} />
                </div>
                <div className="ws-detail-footer"><p>Start with one team and one workflow.</p><Link to="/#request-demo">Discuss this workflow <span aria-hidden="true">↗</span></Link></div>
              </div>}
            </article>;
          })}
        </div>
        {filtered.length === 0 && <div className="ws-empty"><h3>No exact match? The handoff may still be familiar.</h3><p>Try a broader term, clear a filter, or bring us the process your team is dealing with.</p><button type="button" onClick={reset}>Browse all scenarios</button></div>}
        {filtered.length > 6 && <button className="ws-more" type="button" onClick={() => { setShowAll(!showAll); if (showAll) setExpanded(null); }}>{showAll ? 'Show fewer scenarios' : `Explore all ${filtered.length} scenarios`} <span aria-hidden="true">{showAll ? '−' : '+'}</span></button>}

        <div className="ws-bottom"><div><h3>Can't find your process here?</h3><p>Describe the task your team keeps chasing. We can help work out whether it is a good candidate for automation.</p></div><Link to="/#request-demo">Let's map your workflow <span aria-hidden="true">↗</span></Link></div>
      </div>
    </section>
  );
}
